import { FC } from "react"
import retrieveAxiosErr from "~/utils/retrieveAxiosErr"
import { ReduxState, useSafeSetState } from "ra-core"
import { useSelector } from "react-redux"
import { SupportedResources } from "~/dataProvider/tools"
import { useResourceRegister } from "~/utils/resourceRegister"
import { ResetPasswordResponseDto } from "~/gen/api/user"
import { useLocation, Link as RouterLink } from "react-router-dom"
import { parse, pickQueries } from "~/utils/queryStringWrapper"
import { useEffect } from "react"
import LayoutPlain from "./LayoutPlain"
import { Helmet } from 'react-helmet-async'
import { Box, Button, Card, Container, Typography } from "@mui/material"
import LoadingScreen from "~/components/LoadingScreen"
import ResetPasswordCollectForm from "~/components/authentication/ResetPasswordCollectForm"
import ClientError from "../ClientError"
import CreateOrEdit from "~/controller/CreateOrEdit"
import { useSimpleSaveAction } from "~/controller/SaveContextProvider"


const resource: SupportedResources = 'resetpassword-validate'


const ResetPasswordCollect: FC = (props) => {
  useResourceRegister({ name: resource })

  const validateResult = useSelector<ReduxState, any>(store => store.admin.resources[resource]?.data[-1])
  return (
    <CreateOrEdit
      resource={resource} basePath=""
      id="-1"
      record={validateResult}
    >
      <ResetPasswordCollectCater {...props} />
    </CreateOrEdit>
  )
}

const ResetPasswordCollectCater: FC = (props) => {
  const save = useSimpleSaveAction()
  const location = useLocation()

  const [ states, setStates ] = useSafeSetState({
    code: null as string|null,
    cid: null as string|null,
    error: null as any,
    validateResult: undefined as ResetPasswordResponseDto | undefined,
  })

  useEffect(() => {
    if (!location.search) { return }
    setStates(v => {
      if (v.code) { return v }
      const pick = pickQueries(parse(location.search))
      const code = pick('code') || 'never'
      const cid = pick('cid') || 'never'
      if (code) {
        const sendValidate = save
        sendValidate({ code, cid }, {
          onSuccess: ({data}: {data:ResetPasswordResponseDto}) => {
            setStates(v1 => ({ ...v1, validateResult: data }))
          },
          onFailure: (err:any) => {
            setStates(v1 => ({ ...v1, error: err }))
          }
        })
      }
      return { ...v, code, cid, }
    })
  }, [location.search, save, setStates])


  // email was not decoded by backend from code yet
  if (!states.validateResult?.email) {
    // or, the code is invalid/expired
    if (states.error) {
      return (
        <LayoutPlain>
          <ClientError 
            errorNumber={400}
            errorMsg={retrieveAxiosErr(states.error)}
            shortTitle="Password Recovery"
            escapeEntry={
              <Button
                color="primary"
                component={RouterLink}
                to="/login"
                variant="outlined"
              >
                Take me to Sign In properly
              </Button>
            }
          />
        </LayoutPlain>
      )
    } else {
      return (
        <LoadingScreen />
      )
    }
  }
  return (
    <LayoutPlain>
      <Helmet>
        <title>Password Recovery | OnData</title>
      </Helmet>
      <Box>
        <Typography
          color="textPrimary"
          sx={{ 
            fontWeight: 'fontWeightBold',
            textAlign: 'center',
            pb: 3,
          }}
          variant="h5"
        >
          Please choose a new password for your account.
        </Typography>

        <Card
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              lg: 'repeat(2, 1fr)',
              xs: 'repeat(1, 1fr)'
            },
            // minHeight: '100%'
          }}
        >
          <Box sx={{ pt: 8 }} >
            <Container
              maxWidth="md"
              sx={{
                pl: {
                  lg: 15
                }
              }}
            >
              <Typography
                sx={{ color: 'primary.main', pb: 3 }}
                variant="h6"
              >
                Password Guidelines
              </Typography>
              <Typography
                color="textPrimary"
                sx={{ py: 0.5 }}
                variant="body1"
              >
                1. Password must be 12 or more characters long.
              </Typography>
              <Typography
                color="textPrimary"
                sx={{ py: 0.5 }}
                variant="body1"
              >
                2. Password must contain uppercase and lowercase characters, numbers and special characters.
              </Typography>
            </Container>
          </Box>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              pt: 8,
              pb: 3,
            }}
          >
            <Container
              maxWidth="md"
              sx={{
                pr: {
                  lg: 8
                }
              }}
            >
              <Box sx={{}}>
                <ResetPasswordCollectForm code={states.code || 'never'} cid={states.cid || 'never'} email={states.validateResult.email} />
              </Box>
            </Container>
          </Box>
        </Card>
      </Box>
    </LayoutPlain>
  )
}

export default ResetPasswordCollect;
